import { mount, unmount, type Component } from 'svelte';

interface BaseWidgetConfig {
	containerId?: string;
	cssPath?: string; // Path to the widget's CSS file, defaults to auto-detection
}

/**
 * Widget instance - manages lifecycle of a single embedded widget mount.
 */
class Widget<TProps extends Record<string, any>> {
	private shadowRoot: ShadowRoot;
	private svelteComponent: unknown;
	private container: HTMLElement;

	constructor(
		RootComponent: Component<TProps, any, any>,
		container: HTMLElement,
		props: TProps,
		scriptFileBase: string,
		cssPath?: string
	) {
		this.container = container;

		// Create Shadow DOM for style isolation
		this.shadowRoot = container.attachShadow({ mode: 'open' });

		// Inject CSS styles
		const styleSheet = document.createElement('link');
		styleSheet.rel = 'stylesheet';

		// Auto-detect CSS path if not provided
		if (cssPath) {
			styleSheet.href = cssPath;
		} else {
			// Find this widget's own script tag by its exact file base (e.g. "widget"
			// vs "widget-antonio") - a substring match would also match the other
			// widget's script when both are embedded on the same page.
			const scripts = Array.from(document.querySelectorAll('script'));
			const widgetScript = scripts.find((s) => {
				const fileBase = s.src
					.substring(s.src.lastIndexOf('/') + 1)
					.replace(/\.umd\.js$|\.js$/, '');
				return fileBase === scriptFileBase;
			});
			if (widgetScript) {
				const scriptUrl = new URL(widgetScript.src);
				const basePath = scriptUrl.pathname.substring(0, scriptUrl.pathname.lastIndexOf('/'));
				styleSheet.href = `${scriptUrl.origin}${basePath}/${scriptFileBase}.css`;
			} else {
				// Fallback to same directory as HTML
				styleSheet.href = `./dist-${scriptFileBase}/${scriptFileBase}.css`;
			}
		}

		this.shadowRoot.appendChild(styleSheet);

		styleSheet.onload = () => {
			console.log('Styles loaded! Mounting widget...');
			// Create mount point for Svelte component
			const mountPoint = document.createElement('div');
			mountPoint.id = 'widget-mount';
			mountPoint.style.width = '100%';
			mountPoint.style.height = '100%';
			this.shadowRoot.appendChild(mountPoint);
			// Mount Svelte component
			this.svelteComponent = mount(RootComponent, {
				target: mountPoint,
				props
			});
		};
	}

	/**
	 * Destroy the widget instance
	 */
	destroy() {
		if (this.svelteComponent) {
			unmount(this.svelteComponent as never);
		}

		this.shadowRoot.innerHTML = '';
	}
}

/**
 * Builds a global widget singleton API — the same Shadow-DOM-mount mechanics
 * shared by every embeddable widget in this app, parameterized by the root
 * component to mount and its default container id.
 */
export function createWidgetSingleton<TProps extends Record<string, any>>(
	RootComponent: Component<TProps, any, any>,
	defaultContainerId: string,
	scriptFileBase: string
) {
	const instances = new Map<string, Widget<TProps>>();

	return {
		/**
		 * Initialize a new widget instance
		 */
		init(config: TProps & BaseWidgetConfig): Widget<TProps> {
			const { containerId = defaultContainerId, cssPath, ...props } = config;

			// Cleanup existing instance if re-initializing
			if (instances.has(containerId)) {
				instances.get(containerId)!.destroy();
			}

			// Find container element
			const container = document.getElementById(containerId);
			if (!container) {
				throw new Error(`Container element with id "${containerId}" not found`);
			}

			// Create widget instance
			const widget = new Widget(RootComponent, container, props as TProps, scriptFileBase, cssPath);
			instances.set(containerId, widget);
			return widget;
		},

		/**
		 * Destroy a widget instance
		 */
		destroy(containerId: string = defaultContainerId) {
			const widget = instances.get(containerId);
			if (widget) {
				widget.destroy();
				instances.delete(containerId);
			}
		}
	};
}
